import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
  signal
} from "@angular/core";
import { FormGroup, ReactiveFormsModule } from "@angular/forms";
import { TranslateModule } from "@ngx-translate/core";
import { MatRadioGroup } from "@angular/material/radio";
import {
  DealerResponse,
  PackageResponse,
  PackageTypeEnum,
  VehicleSearchResponse,
  WorkshopSystemEnum
} from "src/domain/client";
import { FormService } from "src/app/services/form.service";
import { IconService } from "src/app/services/icon.service";
import { InfoCardTypesEnum } from "src/app/classes/enums";
import { SectionTogglerComponent } from "../../layout/section-toggler/section-toggler.component";
import { PackageContainerComponent } from "../../layout/package-container/package-container.component";
import { InfoCardComponent } from "../../layout/info-card/info-card.component";
import { PackagesServiceFilterPipe } from "src/app/pipes/packages-service-filter.pipe";

@Component({
  selector: "app-specs-service-section",
  templateUrl: "./specs-service-section.component.html",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    SectionTogglerComponent,
    ReactiveFormsModule,
    MatRadioGroup,
    PackageContainerComponent,
    InfoCardComponent,
    TranslateModule
  ]
})
export class SpecsServiceSectionComponent {
  packages = input.required<PackageResponse[]>();
  carInfo = input<VehicleSearchResponse | null>(null);
  selectedDealer = input<DealerResponse | null>(null);
  resetDealers = output<void>();

  toggle = signal(false);
  WorkshopSystemEnum = WorkshopSystemEnum;
  InfoCardTypeEnum = InfoCardTypesEnum;
  firstTimeSelect = true;

  constructor(
    public formService: FormService,
    public iconService: IconService,
    private packageServiceFilterPipe: PackagesServiceFilterPipe
  ) {}

  get form(): FormGroup {
    return this.formService.chooseSpecificationsForm.controls
      .serviceForm as FormGroup;
  }

  get servicePackages(): PackageResponse[] {
    return this.packageServiceFilterPipe.transform(this.packages());
  }

  getPackageFormGroup(id: string): FormGroup {
    return this.form.get(id) as FormGroup;
  }

  isMatchingServiceAgreement(): boolean {
    if (!this.selectedDealer() || !this.carInfo()) return true;
    if (!this.carInfo()?.serviceAgreementResponse?.data?.dealerAgreement)
      return true;
    return this.carInfo()?.serviceAgreementResponse?.data?.validDealers.includes(
      this.selectedDealer().number
    );
  }

  selectService(): void {
    const service = this.packageServiceFilterPipe
      .transform(this.packages())
      .find((x) => x.type === PackageTypeEnum.OriginalService);
    if (service) {
      this.form.get("selectedContainer")?.setValue(service.id);
      this.firstTimeSelect = false;
    }
  }

  getOriginalServiceName(
    pkg: PackageResponse,
    servicePackages: PackageResponse[]
  ): string {
    if (!this.firstTimeSelect) return null;
    if (
      !servicePackages.map((j) => j.type).includes(PackageTypeEnum.BaseService)
    )
      return null;
    const originalService = servicePackages.find(
      (x) => x.type === PackageTypeEnum.OriginalService
    );
    const selected = this.form.get("selectedContainer");
    if (selected?.value === originalService?.id) return null;
    return pkg === originalService ? originalService.name : null;
  }

  collapseService(
    pkg: PackageResponse,
    servicePackages: PackageResponse[]
  ): boolean {
    if (!this.firstTimeSelect) return false;
    if (
      !servicePackages.map((j) => j.type).includes(PackageTypeEnum.BaseService)
    )
      return false;
    const selected = this.form.get("selectedContainer");
    if (selected?.value === pkg?.id) return false;
    const originalService = servicePackages.find(
      (x) => x.type === PackageTypeEnum.OriginalService
    );
    return pkg?.id === originalService?.id;
  }
}
